import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { SortableHandle } from 'react-sortable-hoc';
import { Span } from '@workramp/ui';
import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { academyCustomCSS } from '~/constants/academyCustomCSS';
import { HIDES_CONTENT, VIEWS_CONTENT } from '~/constants/segmentActions';
import { segmentTracking } from '~/common/segment/track';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { common, theme as legacyTheme, media } from '~/styles';
import TimeUtils from '../../../common/time-utils';
import AcademyCertGuideOverview from '../learner/academy-cert-guide-overview';
import WRModalsAlert from '../../wr-modals/wr-modals-alert';
import { color, certOverviewContent } from './academy-styles';
import AcademyIconSvgGuide from './academy-icon-svg-guide';
import AcademyIconSvgResource from './academy-icon-svg-resource';
import AcademyIconSvgTlc from './academy-icon-svg-tlc';
import AcademyIconSvgEvent from './academy-icon-svg-event';
import { CertPassedIcon, CertFailedIcon, CertRequiresGradingIcon } from './academy-certification-icons';

export default class AcademyCertificationLearnerContentItem extends Component {
  TRANSLATED_SHOW_CONTENT = I18n.t('js.academy.certifications.content_item.show_content');

  TRANSLATED_HIDE_CONTENT = I18n.t('js.academy.certifications.content_item.hide_content');

  TRANSLATED_LOCKED_PATH_ALERT = I18n.t('js.academy.certifications.content_item.locked_path_alert');

  TRANSLATED_SHOW_HIDE_TOGGLE_LABEL = (toggleText, displayText) =>
    I18n.t('js.academy.certifications.content_item.toggle_label', {
      toggleText,
      displayText,
    });

  state = {
    contentExpanded: false,
    showModal: false,
  };

  render() {
    const { certificationContent, isEditing, lastItem, renderDots } = this.props;
    const { contentType } = certificationContent;
    const { showModal } = this.state;
    const wrapperColor = this.progressStatusColor(certificationContent);

    return (
      <>
        <CardWrapperWithDots role="listitem" className={academyCustomCSS.overview.moduleBoxOuterWrapper}>
          <Wrapper className={academyCustomCSS.overview.moduleBox} color={wrapperColor}>
            {this.renderSummary()}
            {contentType === AssetPickerTypes.GUIDE && this.renderDetailedContent()}
            {contentType === AssetPickerTypes.GUIDE && this.renderExpandContentToggle()}
            {isEditing && this.renderDeleteIcon()}
          </Wrapper>

          {!lastItem && renderDots()}
        </CardWrapperWithDots>

        <WRModalsAlert
          text={this.TRANSLATED_LOCKED_PATH_ALERT}
          show={showModal}
          onClose={() => this.setState({ showModal: false })}
        />
      </>
    );
  }

  renderSummary = () => {
    const { certificationContent } = this.props;
    const { assignment, content } = certificationContent;
    const isComplete = assignment && assignment.completedAt;
    const isAttendedEvent =
      certificationContent.contentType === AssetPickerTypes.ACADEMY_EVENT && certificationContent.attended === true;

    return (
      <SummaryWrapper className={academyCustomCSS.overview.moduleBoxInnerWrapper}>
        {this.renderTypeIcon()}
        {this.renderTitleAndDescription()}
        {isComplete ? this.renderPassingInfo() : content.timeEstimateSeconds > 0 && this.getTimeEstimate()}
        {isAttendedEvent && this.renderEventAttended(content?.id)}
      </SummaryWrapper>
    );
  };

  renderEventAttended = ({ id }) => (
    <PassingInfoWrapper className={academyCustomCSS.overview.modulePassingStatus}>
      <CertPassedIcon />
      <ReactTooltip positionStrategy="fixed" id={id} place="bottom" effect="solid" />
    </PassingInfoWrapper>
  );

  renderDetailedContent = () => {
    const { certificationContent, themeOptions } = this.props;
    const { contentExpanded } = this.state;

    return (
      <div>
        <AcademyCertGuideOverview
          guide={certificationContent.content}
          guideExpanded={contentExpanded}
          assignment={certificationContent.assignment}
          themeOptions={themeOptions}
        />
      </div>
    );
  };

  renderTitleAndDescription = () => (
    <TitleAndDescriptionWrapper>
      {this.renderTitle()}
      {this.props.certificationContent.content.description && this.renderDescription()}
    </TitleAndDescriptionWrapper>
  );

  renderExpandContentToggle = () => {
    const { contentExpanded } = this.state;
    const { displayTitle } = this.props.certificationContent.content;

    return (
      <ExpandContentWrapper className={academyCustomCSS.overview.contentToggleWrapper}>
        <ExpandContentToggle
          className={academyCustomCSS.overview.contentToggle}
          expanded={contentExpanded}
          onClick={() => this.toggleShowContent()}
          type="button"
          aria-label={this.TRANSLATED_SHOW_HIDE_TOGGLE_LABEL(
            contentExpanded ? this.TRANSLATED_HIDE_CONTENT : this.TRANSLATED_SHOW_CONTENT,
            displayTitle,
          )}
        >
          {contentExpanded ? this.TRANSLATED_HIDE_CONTENT : this.TRANSLATED_SHOW_CONTENT}
        </ExpandContentToggle>
      </ExpandContentWrapper>
    );
  };

  toggleShowContent = () => {
    const { contentExpanded } = this.state;
    this.setState({ contentExpanded: !contentExpanded });
    this.segmentTrackShowContent();
  };

  segmentTrackShowContent = () => {
    const { contentExpanded } = this.state;
    const {
      certificationContent: { content },
    } = this.props;
    const segmentEventName = contentExpanded ? HIDES_CONTENT : VIEWS_CONTENT;
    const segmentPayload = {
      segmentEventName,
      properties: {
        content: {
          id: content.id,
          name: content.title,
        },
      },
    };
    segmentTracking(segmentPayload);
  };

  renderDragHandle = () => {
    const DragHandle = SortableHandle(() => (
      <DragHandleIcon className="task-object-drag task-object-icon allow-draggable" src="/images/move_hover_icon.svg" />
    ));

    return <DragHandle />;
  };

  renderDeleteIcon = () => (
    <DeleteIcon
      onClick={() => this.props.onDeleteClick(this.props.certificationContent)}
      src="/images/cross_icon_grey.svg"
    />
  );

  renderTypeIcon = () => {
    const { contentType } = this.props.certificationContent;
    let typeIcon;
    const progressStatusColor = this.progressStatusColor(this.props.certificationContent);

    if (_.contains(APP_CONSTANTS.TOP_LEVEL_CONTENT_IN_ACADEMIES, contentType)) {
      return (
        <TypeIconWrapper>
          <AcademyIconSvgTlc height={30} color={progressStatusColor} />
        </TypeIconWrapper>
      );
    }

    switch (contentType) {
      case AssetPickerTypes.RESOURCE:
        typeIcon = <AcademyIconSvgResource height={30} color={progressStatusColor} />;
        break;
      case AssetPickerTypes.GUIDE:
        typeIcon = <AcademyIconSvgGuide height={30} color={progressStatusColor} />;
        break;
      case AssetPickerTypes.ACADEMY_EVENT:
        typeIcon = <AcademyIconSvgEvent height={30} color={progressStatusColor} />;
        break;
    }

    return <TypeIconWrapper>{typeIcon}</TypeIconWrapper>;
  };

  renderTitle = () => {
    const { certificationContent, isEditing, pageSlug } = this.props;
    const { academyCertificationId } = certificationContent;
    const title = certificationContent.content.title || certificationContent.content.name;

    let href;
    if (isEditing) {
      // admin
      href = AssetPickerTypes.getItemAdminUrl({
        type: certificationContent.contentType,
        id: certificationContent.contentId,
      });
    } else {
      const contentHasOverview = [...APP_CONSTANTS.TOP_LEVEL_CONTENT_IN_ACADEMIES, AssetPickerTypes.RESOURCE].includes(
        certificationContent.contentType,
      );

      const urlPrefix = pageSlug ? `/pages/${pageSlug}` : '';
      if (certificationContent.contentType === 'academy_event') {
        href = `${urlPrefix}/events/${certificationContent.contentId}?certification_id=${academyCertificationId}`;
      } else {
        href = `${urlPrefix}/training/${certificationContent.id}/${
          contentHasOverview ? 'register' : 'overview'
        }?certification_id=${academyCertificationId}`;
      }
    }

    const target = certificationContent.content.openLinkDirectlyInNewTab ? '_blank' : '_self';
    return (
      <div>
        <Title
          className={academyCustomCSS.overview.moduleTitle}
          href={href}
          onClick={(event) => this.handleShowModal({ event, title, url: href, target })}
          target={target}
          role="heading"
          aria-level="3"
        >
          {title}
        </Title>
      </div>
    );
  };

  renderDescription = () => (
    <DescriptionWrapper className={academyCustomCSS.overview.moduleDescription}>
      <Span isExpandable maxLines={1}>
        {this.props.certificationContent.content.description}
      </Span>
    </DescriptionWrapper>
  );

  renderPassingInfo = () => {
    const { certificationContent } = this.props;
    const { assignment, content } = certificationContent;

    const TRANSLATED_PASSING_SCORE = I18n.t('js.academy.certifications.content_item.passing_score', {
      score: content.passingScorePct,
    });
    const TRANSLATED_SCORE = I18n.t('js.academy.certifications.content_item.score', { score: assignment.score });
    const TRANSLATED_CURRENT_SCORE = I18n.t('js.academy.certifications.content_item.current_score', {
      score: assignment.score,
    });

    const passingInfoProps = {};
    if (content.hasOwnProperty('passingScorePct') && content.passingScorePct !== null) {
      passingInfoProps['data-tooltip-content'] = TRANSLATED_PASSING_SCORE;
      passingInfoProps['data-tooltip-id'] = content.id;
    }
    const { didPass, didFail, doesRequireGrading } = assignment.passStatusMetadata;

    return (
      <PassingInfoWrapper {...passingInfoProps} className={academyCustomCSS.overview.modulePassingStatus}>
        {didPass && <CertPassedIcon />}
        {didFail && <CertFailedIcon />}
        {doesRequireGrading && <CertRequiresGradingIcon />}
        {assignment.score !== null && (
          <ScoreWrapper>{doesRequireGrading ? TRANSLATED_CURRENT_SCORE : TRANSLATED_SCORE}</ScoreWrapper>
        )}
        {/*  TODO: fix styling w/score */}
        <ReactTooltip positionStrategy="fixed" id={content.id} place="bottom" effect="solid" />
      </PassingInfoWrapper>
    );
  };

  getTimeEstimate = () => {
    const TRANSLATED_DURATION = I18n.t('js.academy.certifications.module.duration');

    return (
      <TimeEstimateWrapper className={academyCustomCSS.overview.moduleDuration}>
        <TimeEstimateIcon
          src="/images/time_estimate_icon_grey.svg"
          width="12px"
          height="12px"
          alt={TRANSLATED_DURATION}
        />
        <TimeEstimateText>
          {TimeUtils.displayDurationShort(this.props.certificationContent.content.timeEstimateSeconds)}
        </TimeEstimateText>
      </TimeEstimateWrapper>
    );
  };

  assignmentCompleted = () => {
    const { assignment } = this.props.certificationContent;
    return assignment && assignment.completedAt;
  };

  progressStatusColor = (certificationContent) => {
    // TODO: handle "requires manual grading" status color
    const { assignment } = certificationContent;
    if (certificationContent.contentType === AssetPickerTypes.ACADEMY_EVENT && certificationContent.attended === true) {
      return color.certPassed;
    }
    if (assignment) {
      const { didPass, didFail, doesRequireGrading } = assignment.passStatusMetadata;
      if (didPass) return color.certPassed;
      if (didFail) return color.certFailed;
      if (doesRequireGrading) return color.certRequiresGrading;

      // if there is an assignment that is neither passed, failed, nor requires grading, it must be in progress
      return color.inProgress;
    }
    return color.neutralGrey;
  };

  handleShowModal = ({ event, url, target }) => {
    const { isLocked, isEditing, openRecertificationModal, canRecertify } = this.props;
    event.preventDefault();

    if (isFeatureEnabled('locked_academy_paths') && isLocked && !isEditing) {
      this.setState({ showModal: true });
      return false;
    }

    if (!isEditing && canRecertify) {
      openRecertificationModal(event);
      return false;
    }

    return window.open(url, target);
  };
}

const CardWrapperWithDots = styled.div`
  position: relative;
  margin-bottom: 5rem;

  @media print {
    display: none;
  }
`;

const Wrapper = styled.div`
  margin-left: 15%;
  width: 70%;

  background-color: white;
  border-top: 8px solid ${(props) => props.color};

  box-sizing: border-box;

  border-radius: 0 0 3px 3px;
  box-shadow: 0 0px 10px 0 rgba(0, 0, 0, 0.1);

  ${media.mobile} {
    margin-left: 8%;
    width: 84%;
  }
`;

const SummaryWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem ${certOverviewContent.paddingLeftRight};

  ${media.desktopMedium} {
    padding: ${certOverviewContent.paddingLeftRightDesktopMedium};
  }

  ${media.mobile} {
    padding: ${certOverviewContent.paddingLeftRightMobile};
    flex-direction: column;
  }
`;

const DragHandleIcon = styled.img`
  margin-right: 15px;
  width: 9px;
  height: 13px;
  cursor: move;
`;

const DeleteIcon = styled.img`
  height: 10px;
  width: 10px;
  margin-left: 10px;
  cursor: pointer;
`;

const TypeIconWrapper = styled.span`
  margin-right: calc(2rem + 10px);

  ${media.mobile} {
    margin-right: 0;
  }
`;

const TitleAndDescriptionWrapper = styled.div`
  display: flex;
  flex: 75%;
  flex-direction: column;
  justify-content: space-between;
  ${common.hideOverflow}
  width: 100%;
  padding-right: 15px;

  ${media.mobile} {
    text-align: center;
    margin-top: 0.5rem;
    ${common.unsetOverflow}
    padding-right: 0;
  }
`;

const Title = styled.a`
  color: ${({ theme }) => theme.colors.on[1000]} !important;
  ${common.hideOverflow}
  font-weight: 700;
  font-size: 1.2rem;
  display: block;
  margin-top: 10px;
  &:visited {
    color: ${({ theme }) => theme.colors.on[1000]};
  }
  &:hover {
    text-decoration: none;
  }

  ${media.mobile} {
    ${common.unsetOverflow}
  }
`;

const DescriptionWrapper = styled.span`
  margin-top: 0.5rem;
  color: ${legacyTheme.textDescription};
  font-size: 0.9rem;
  ${common.unsetOverflow}

  ${media.mobile} {
    margin-top: 0.5rem;
  }
`;

const TimeEstimateWrapper = styled.div`
  white-space: nowrap;
  ${media.mobile} {
    margin-top: 0.5rem;
  }
`;

const TimeEstimateIcon = styled.img`
  margin-right: 4px;
`;

const TimeEstimateText = styled.span`
  color: ${legacyTheme.textDescription};
`;

const ExpandContentWrapper = styled.div`
  border-top: 1px solid #d8d8d8;
  padding: 1rem ${certOverviewContent.paddingLeftRight} 1rem calc(${certOverviewContent.paddingLeftRight} + 20px);
  color: ${({ theme }) => theme.colors.on[1000]};

  ${media.desktopMedium} {
    padding-left: calc(${certOverviewContent.paddingLeftRightDesktopMedium} + 20px);
  }

  ${media.mobile} {
    padding-left: calc(${certOverviewContent.paddingLeftRightMobile} + 20px);
  }
`;

const ExpandContentToggle = styled.button`
  font-weight: 600;
  font-size: 0.9rem;
  position: relative;
  cursor: pointer;
  background: transparent;
  border: none;

  &:before {
    content: '▾';
    display: block;
    position: absolute;
    left: -20px;
    top: 50%;
    -webkit-transform: translateY(-50%) ${(props) => (props.expanded ? 'rotate(-180deg)' : '')};
    -ms-transform: translateY(-50%) ${(props) => (props.expanded ? 'rotate(-180deg)' : '')};
    transform: translateY(-50%) ${(props) => (props.expanded ? 'rotate(-180deg)' : '')};
    -webkit-transition: all 0.2s ease-in-out;
    transition: all 0.2s ease-in-out;
  }
`;

const PassingInfoWrapper = styled.div`
  display: flex;
  flex: 25%;
  flex-direction: column;
  align-items: center;
  /* min-width: 4rem; */

  ${media.mobile} {
    margin-top: 1rem;
  }
`;

const ScoreWrapper = styled.div`
  font-size: 0.8rem;
  margin-top: 0.25rem;
`;
